import React, { Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";

// Auth centralisé RBAC
import { AuthProvider, useAuth, ROLES } from "./Context/AuthContext";
import { CartProvider } from "./Context/CartContext";

// Garde RBAC générique + anciens guards (backward compat)
import RoleRoute from "./Components/common/ProtectedRoute/RoleRoute";
import AdminProtectedRoute from "./Components/common/ProtectedRoute/AdminProtectedRoute";
import ProtectedPublic from "./Components/common/ProtectedRoute/ProtectedPublic";
import ErrorBoundary from "./Components/common/ErrorBoundary/ErrorBoundary";

// Layout (toujours présent → eager)
import Header from "./Components/common/Header/Header";
import Footer from "./Components/common/Footer/Footer";
import BottomNav from "./Components/common/BottomNav/BottomNav";
import Alert from "./Components/UI/Alert/Alert";
import { ToastProvider, ToastInitializer } from "./Components/UI/Toast/Toast";
import { ConfirmProvider } from "./Components/UI/ConfirmDialog/ConfirmDialog";
import ScrollToTopButton from "./Components/common/ScrollToTop/ScrollToTopButton";
import InactivityTimer from "./Components/InactivityTimer/InactivityTimer";
import useSessionPolicy from "./hooks/useSessionPolicy"; // S1 — délai d'inactivité fourni par le serveur

// API
import { checkAPIHealth } from "./api/apiService";
import { usePageTracking } from "./hooks/usePageTracking";

// Styles globaux
import "./stylesApp/Style.css";

// =============================================
// LAZY LOADING - chaque route chargée à la demande
// =============================================

// Page d'accueil (chemin critique, chargée en premier)
const Home = React.lazy(() => import("./modules/static/pagesStatic/Home"));

// AUTH clients
const Login             = React.lazy(() => import("./modules/authG/pagesAuth/Login"));
const Signup            = React.lazy(() => import("./modules/authG/pagesAuth/Signup"));
const GoogleAuthSuccess = React.lazy(() => import("./modules/authG/pagesAuth/GoogleAuthSuccess"));
const SecurityNotMe = React.lazy(() => import("./modules/authG/pagesAuth/SecurityNotMe"));
const OtpVerify         = React.lazy(() => import("./modules/authG/pagesAuth/OtpVerify"));
const VerifyEmail       = React.lazy(() => import("./modules/authG/pagesAuth/VerifyEmail"));
const ForgotPassword    = React.lazy(() => import("./modules/authG/pagesAuth/ForgotPassword"));
const ResetPassword     = React.lazy(() => import("./modules/authG/pagesAuth/ResetPassword"));

// Profil client
const Profile = React.lazy(() => import("./modules/profile/pagesProfile/Profile"));

// Trajets
const Travels = React.lazy(() => import("./modules/Travels/pagesTravels/Travels"));

// Réservations
const MyReservations = React.lazy(() => import("./modules/reservations/MyReservations"));

// Panier
const CartPage = React.lazy(() => import("./Components/CartPage/CartPage"));

// Paiement
const PaiementPage        = React.lazy(() => import("./modules/paiement/PaiementPage"));
const PaiementSuccessPage = React.lazy(() => import("./modules/paiement/PaiementSuccessPage"));
const CheckoutPage        = React.lazy(() => import("./modules/paiement/CheckoutPage"));
const PaiementEnAttente   = React.lazy(() => import("./modules/paiement/PaiementEnAttente"));
const PaiementCancel      = React.lazy(() => import("./modules/paiement/PaiementCancel"));

// Pages statiques (rarement visitées - chargées en dernier)
const Contact            = React.lazy(() => import("./modules/static/pagesStatic/Contact"));
const Partenaire         = React.lazy(() => import("./modules/static/pagesStatic/Partenaire"));
const About              = React.lazy(() => import("./modules/static/pagesStatic/About"));
const FAQ                = React.lazy(() => import("./modules/static/pagesStatic/FAQ"));
const FormulaireTransport = React.lazy(() => import("./modules/static/pagesStatic/FormulaireTransport"));
const MentionsLegales    = React.lazy(() => import("./modules/static/pagesStatic/MentionsLegales"));
const VerifyTicket       = React.lazy(() => import("./modules/static/pagesStatic/VerifyTicket"));
const RetrouverBillet    = React.lazy(() => import("./modules/static/pagesStatic/RetrouverBillet"));
const PrivacyPolicy      = React.lazy(() => import("./modules/static/pagesStatic/PrivacyPolicy"));
const Terms              = React.lazy(() => import("./modules/static/pagesStatic/Terms"));
const Maintenance        = React.lazy(() => import("./modules/static/pagesStatic/Maintenance"));
const ChauffeurScan      = React.lazy(() => import("./modules/shared/ChauffeurScan"));
const ScanMoved          = React.lazy(() => import("./modules/driver/pagesDriver/ScanMoved"));
const AvisPage           = React.lazy(() => import("./modules/static/pagesStatic/AvisPage"));

// Admin (utilisé par ~5% des utilisateurs)
const AdminSignup    = React.lazy(() => import("./modules/admin/pagesAdmin/AdminSignup"));
// L'écran de connexion Company/Admin passe par AdminPortal (= écran unique → AdminLogin).
const AccessPortal   = React.lazy(() => import("./modules/admin/pagesAdmin/AdminPortal"));
const AdminDashboard = React.lazy(() => import("./modules/admin/pagesAdmin/AdminDashboard"));

// Driver — espace chauffeur isolé
const DriverDashboard = React.lazy(() => import("./modules/driver/pagesDriver/DriverDashboard"));
const DriverTrajets = React.lazy(() => import("./modules/driver/pagesDriver/DriverTrajets"));
const DriverProfile = React.lazy(() => import("./modules/driver/pagesDriver/DriverProfile"));
const InternalMessaging = React.lazy(() => import("./modules/messaging/InternalMessaging"));

// Company — espace compagnie isolé (wraps AdminDashboard scopé)
const CompanyDashboard = React.lazy(() => import("./modules/company/pagesCompany/CompanyDashboard"));

// =============================================
// FALLBACKS
// =============================================

const GlobalLoader = () => null;

const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
};

// 🔒 ISOLATION DRIVER — les routes /driver et /chauffeur ne doivent PAS être servies par le
// domaine public. Sur gowele.com / www.gowele.com (prod), on redirige 301-like vers le domaine
// officiel driver.gowele.com en conservant le chemin. En dev/preview (localhost, *.vercel.app)
// on rend le composant normalement pour ne pas casser le développement.
const DriverDomainRedirect = ({ children }) => {
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  const isPublicProd = h === 'gowele.com' || h === 'www.gowele.com';
  useEffect(() => {
    if (isPublicProd) {
      window.location.replace('https://driver.gowele.com' + window.location.pathname + window.location.search);
    }
  }, [isPublicProd]);
  if (isPublicProd) return null;
  return children;
};

// 🏠 Accueil RÉEL selon le portail (hostname). Sur admin/company, « / » redirige vers /admin/login
// (route racine du portail) : on renvoie donc vers /admin (dashboard), jamais « / » qui = login.
const portalHomePath = () => {
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h === 'admin.gowele.com' || h === 'company.gowele.com' || h.startsWith('gowele-admin.') || process.env.REACT_APP_ADMIN_SITE === 'true') return '/admin';
  if (h === 'driver.gowele.com') return '/driver';
  return '/';
};

const PageNotFound = () => {
  const { t } = useTranslation('common');
  return (
    <div className="page-not-found">
      <h2>{t('notFound.title', '404 — Page introuvable')}</h2>
      <p>{t('notFound.message', 'Cette page n\'existe pas ou a été déplacée.')}</p>
      <a href={portalHomePath()} className="link-home">{t('notFound.backHome', 'Retour à l\'accueil')}</a>
    </div>
  );
};

const PageUnauthorized = () => {
  const { t } = useTranslation('common');
  return (
    <div className="page-not-found">
      <h2>{t('unauthorized.title')}</h2>
      <p>{t('unauthorized.message')}</p>
      <a href={portalHomePath()} className="link-home">{t('unauthorized.backHome')}</a>
    </div>
  );
};

// =============================================
// ROUTES
// =============================================

// =============================================
// DÉTECTION DU DÉPLOIEMENT
// =============================================
const isDriverHost  = () => typeof window !== 'undefined' && window.location.hostname === 'driver.gowele.com';
const isCompanyHost = () => typeof window !== 'undefined' && window.location.hostname === 'company.gowele.com';

const isAdminHost = () => {
  const h = window.location.hostname;
  return (
    process.env.REACT_APP_ADMIN_SITE === 'true' ||
    h === 'admin.gowele.com' ||
    h === 'company.gowele.com' ||   // espace compagnie (même app, login → /admin)
    h === 'driver.gowele.com' ||    // espace chauffeur (même app, login → /driver/dashboard)
    h.startsWith('gowele-admin.')
  );
};

// =============================================
// PORTAIL ADMIN — admin.gowele.com
// Aucun code public chargé, aucun Header/Footer
// =============================================
const AdminPortal = ({ alert, handleAlertClose, maintenance = { active: false, message: '', until: null } }) => {
  const { isAuthenticated, role, logout, loading } = useAuth();
  const { t } = useTranslation('common');
  const { pathname } = useLocation();

  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  // Repli (valeurs actuelles) si le serveur ne fournit pas le délai d'inactivité.
  const fallbackMs = {
    [ROLES.ADMIN_COMPANY]: 30 * 60 * 1000,
    [ROLES.SUPER_ADMIN]:   60 * 60 * 1000,
    [ROLES.DRIVER]:        30 * 60 * 1000,
  };
  const fallbackMinutes = (fallbackMs[role] || 30 * 60 * 1000) / 60000;
  // S1 — lit le délai d'inactivité du SERVEUR (contexte /me, admin-only) ; repli sinon.
  const sessionPolicy = useSessionPolicy({ enabled: isAuthenticated && !loading, role, fallbackMinutes });

  // 🚧 MAINTENANCE DU COMPANY PORTAL (company.gowele.com uniquement).
  // /api/status est conscient de l'origine : sur admin.gowele.com il renvoie TOUJOURS
  // maintenance:false (garde anti-auto-verrouillage) → le Super Admin Portal n'est jamais bloqué.
  // Ici, on n'affiche l'écran que si l'hôte est company ET la maintenance company est active.
  // Filet supplémentaire : un SUPER_ADMIN authentifié n'est jamais verrouillé, même sur ce host.
  const isSuperAdmin = isAuthenticated && role === ROLES.SUPER_ADMIN;
  if (isCompanyHost() && maintenance.active && !isSuperAdmin) {
    return (
      <Suspense fallback={<GlobalLoader />}>
        <Maintenance message={maintenance.message} until={maintenance.until} />
      </Suspense>
    );
  }

  return (
    <>
      <ToastInitializer />

      {isAuthenticated && !loading && (
        <InactivityTimer
          onLogout={logout}
          userLabel={
            role === ROLES.SUPER_ADMIN   ? t('inactivity.superAdmin') :
            role === ROLES.ADMIN_COMPANY ? t('inactivity.company')    :
                                           t('inactivity.driver')
          }
          timeoutMs={sessionPolicy.inactivityMs}
        />
      )}

      {alert.show && (
        <Alert type={alert.type} message={alert.message} onClose={handleAlertClose} autoClose={false} />
      )}

      <Suspense fallback={<GlobalLoader />}>
        {(() => {
          const driverHost  = isDriverHost();
          const companyHost = isCompanyHost();
          // Login dédié : chauffeur → login client (rôle DRIVER), sinon portail (filtré compagnie)
          const loginEl = driverHost
            ? <Login driverMode />
            : <AccessPortal variant={companyHost ? 'company' : 'admin'} />;
          const loginPath = driverHost ? '/driver/login' : '/admin/login';
          return (
        <Routes>
          {/* Entrée — selon le sous-domaine */}
          <Route path="/"            element={<Navigate to={loginPath} replace />} />
          <Route path="/admin/login" element={loginEl} />
          <Route path="/driver/login" element={driverHost ? <Login driverMode /> : <Navigate to="/admin/login" replace />} />
          {/* OTP (login email) — nécessaire sur ce tree sinon le flux OTP échoue (route absente) */}
          <Route path="/otp-verify" element={<OtpVerify />} />
          {/* Mot de passe oublié / réinitialisation — indispensables sur driver/admin/company
              (sinon « mot de passe oublié » tombe sur le catch-all → page blanche). */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/admin/signup" element={
            <AdminProtectedRoute requiredRoles={['SUPER_ADMIN']}>
              <AdminSignup />
            </AdminProtectedRoute>
          } />

          {/* Espace Super Admin */}
          <Route path="/admin/*" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />

          {/* Espace Admin Compagnie — consolidé dans le panel /admin (évite le doublon).
              Les anciennes URL /company/* redirigent vers /admin. */}
          <Route path="/company/*"         element={<Navigate to="/admin" replace />} />

          {/* Espace Chauffeur (accessible depuis admin.gowele.com aussi) */}
          <Route path="/chauffeur"        element={<ChauffeurScan />} />
          <Route path="/driver"           element={<Navigate to="/driver/dashboard" replace />} />
          <Route path="/driver/dashboard" element={<RoleRoute roles={[ROLES.DRIVER]}><DriverDashboard /></RoleRoute>} />
          {/* 🔒 Scan web driver SUSPENDU (option 3) → renvoie vers l'appli mobile. Planning + messagerie conservés. */}
          <Route path="/driver/scan"      element={<RoleRoute roles={[ROLES.DRIVER]}><ScanMoved /></RoleRoute>} />
          <Route path="/driver/trajets"   element={<RoleRoute roles={[ROLES.DRIVER]}><DriverTrajets /></RoleRoute>} />
          <Route path="/driver/profile"   element={<RoleRoute roles={[ROLES.DRIVER]}><DriverProfile /></RoleRoute>} />
          <Route path="/driver/messagerie" element={<RoleRoute roles={[ROLES.DRIVER]}><div style={{ maxWidth: 1100, margin: '0 auto', padding: '1rem' }}><InternalMessaging /></div></RoleRoute>} />
          <Route path="/driver/*"         element={<Navigate to="/driver/dashboard" replace />} />

          {/* Tout le reste → login du sous-domaine */}
          <Route path="*" element={<Navigate to={loginPath} replace />} />
        </Routes>
          );
        })()}
      </Suspense>
    </>
  );
};

// =============================================
// PORTAIL PUBLIC — gowele.com
// Clients, visiteurs — aucune route admin
// =============================================
const PublicPortal = ({ apiStatus, alert, handleAlertClose, maintenance }) => {
  const { isAuthenticated, role, logout, loading } = useAuth();
  const { t } = useTranslation('common');
  const { pathname } = useLocation();

  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  usePageTracking();

  // Header/Footer masqués uniquement pour les espaces driver/chauffeur qui ont leur propre UI.
  // Les routes /user/* gardent le Header global → navigation toujours disponible après login.
  const isProtectedRoute = pathname.startsWith('/driver') || pathname.startsWith('/chauffeur');

  // S1 — délai d'inactivité : CLIENT 15 min / DRIVER 30 min (repli ; /me/context est admin-only donc
  // le hook repliera ici — comportement identique à avant, prêt si un endpoint public arrive).
  const publicFallbackMinutes = role === ROLES.DRIVER ? 30 : 15;
  const publicSessionPolicy = useSessionPolicy({ enabled: isAuthenticated && !loading, role, fallbackMinutes: publicFallbackMinutes });

  const isAdminAuth = isAuthenticated && (role === ROLES.SUPER_ADMIN || role === ROLES.ADMIN_COMPANY);
  if (maintenance.active && !isAdminAuth) {
    return (
      <Suspense fallback={<GlobalLoader />}>
        <Maintenance message={maintenance.message} until={maintenance.until} />
      </Suspense>
    );
  }

  return (
    <>
      <ToastInitializer />

      {isAuthenticated && !loading && (
        <InactivityTimer
          onLogout={logout}
          userLabel={
            role === ROLES.DRIVER ? t('inactivity.driver') : t('inactivity.client')
          }
          timeoutMs={publicSessionPolicy.inactivityMs}
        />
      )}

      {alert.show && (
        <Alert type={alert.type} message={alert.message} onClose={handleAlertClose} autoClose={false} />
      )}

      {!isProtectedRoute && <Header apiStatus={apiStatus} />}

      <main className={isProtectedRoute ? "" : "main-content"}>
        <Suspense fallback={<GlobalLoader />}>
          <Routes>
            {/* ── Pages publiques ── */}
            {/* 🏠 Page d'accueil = Home (hero + trajets populaires + avis clients + compteur).
                La recherche complète des trajets vit sur /travels (route plus bas). */}
            <Route path="/"                    element={<Home />} />
            <Route path="/signup"              element={<Signup />} />
            <Route path="/login"               element={<Login />} />
            <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
            {/* 🔒 Cible du lien « Ce n'est pas moi » de l'email d'alerte de connexion */}
            <Route path="/security/not-me"     element={<SecurityNotMe />} />
            <Route path="/otp-verify"          element={<OtpVerify />} />
            <Route path="/verify-email"        element={<VerifyEmail />} />
            <Route path="/forgot-password"     element={<ForgotPassword />} />
            <Route path="/reset-password"      element={<ResetPassword />} />
            <Route path="/travels"             element={<Travels />} />
            <Route path="/contact"             element={<Contact />} />
            <Route path="/partenaire"          element={<Partenaire />} />
            <Route path="/transport-form"      element={<FormulaireTransport />} />
            <Route path="/about"               element={<About />} />
            <Route path="/faq"                 element={<FAQ />} />
            <Route path="/mentions-legales"    element={<MentionsLegales />} />
            <Route path="/verify-ticket"       element={<VerifyTicket />} />
            <Route path="/verify/:code"        element={<VerifyTicket />} />
            {/* 🎫 Récupération de billet INVITÉ (public, sans compte) : code + téléphone/email */}
            <Route path="/retrouver-billet"    element={<RetrouverBillet />} />
            <Route path="/chauffeur"           element={<DriverDomainRedirect><ChauffeurScan /></DriverDomainRedirect>} />
            <Route path="/avis"                element={<AvisPage />} />
            <Route path="/privacy-policy"      element={<PrivacyPolicy />} />
            <Route path="/terms"               element={<Terms />} />

            {/* ── Espace Client ── */}
            <Route path="/user"                      element={<Navigate to="/user/home" replace />} />
            <Route path="/user/home"                 element={<RoleRoute roles={[ROLES.CLIENT]}><Profile /></RoleRoute>} />
            <Route path="/user/dashboard"            element={<RoleRoute roles={[ROLES.CLIENT]}><Profile /></RoleRoute>} />
            <Route path="/user/profile"              element={<RoleRoute roles={[ROLES.CLIENT]}><Profile /></RoleRoute>} />
            <Route path="/user/reservations"         element={<RoleRoute roles={[ROLES.CLIENT]}><MyReservations /></RoleRoute>} />
            <Route path="/user/panier"               element={<RoleRoute roles={[ROLES.CLIENT]}><CartPage /></RoleRoute>} />
            <Route path="/user/checkout"             element={<RoleRoute roles={[ROLES.CLIENT]}><CheckoutPage /></RoleRoute>} />
            <Route path="/user/paiement/:id"         element={<RoleRoute roles={[ROLES.CLIENT]}><PaiementPage /></RoleRoute>} />
            <Route path="/user/paiement/success/:id" element={<RoleRoute roles={[ROLES.CLIENT]}><PaiementSuccessPage /></RoleRoute>} />
            <Route path="/user/*"                    element={<Navigate to="/user/home" replace />} />

            {/* ── Espace Chauffeur — ISOLÉ : sur le domaine public, tout /driver/* redirige
                vers driver.gowele.com (en dev/preview, rendu normal). Plus d'UI Driver servie
                par gowele.com. ── */}
            <Route path="/driver"           element={<DriverDomainRedirect><Navigate to="/driver/dashboard" replace /></DriverDomainRedirect>} />
            <Route path="/driver/dashboard" element={<DriverDomainRedirect><RoleRoute roles={[ROLES.DRIVER]}><DriverDashboard /></RoleRoute></DriverDomainRedirect>} />
            {/* 🔒 Scan web driver SUSPENDU (option 3) → renvoie vers l'appli mobile. Planning + messagerie conservés. */}
            <Route path="/driver/scan"      element={<DriverDomainRedirect><RoleRoute roles={[ROLES.DRIVER]}><ScanMoved /></RoleRoute></DriverDomainRedirect>} />
            <Route path="/driver/trajets"   element={<DriverDomainRedirect><RoleRoute roles={[ROLES.DRIVER]}><DriverTrajets /></RoleRoute></DriverDomainRedirect>} />
            <Route path="/driver/profile"   element={<DriverDomainRedirect><RoleRoute roles={[ROLES.DRIVER]}><DriverProfile /></RoleRoute></DriverDomainRedirect>} />
            <Route path="/driver/messagerie" element={<DriverDomainRedirect><RoleRoute roles={[ROLES.DRIVER]}><div style={{ maxWidth: 1100, margin: '0 auto', padding: '1rem' }}><InternalMessaging /></div></RoleRoute></DriverDomainRedirect>} />
            <Route path="/driver/*"         element={<DriverDomainRedirect><Navigate to="/driver/dashboard" replace /></DriverDomainRedirect>} />

            {/* ── Admins sur gowele.com → redirigés vers admin.gowele.com ── */}
            <Route path="/admin/*"   element={<ExternalRedirect to="https://admin.gowele.com" />} />
            <Route path="/company/*" element={<ExternalRedirect to="https://admin.gowele.com" />} />

            {/* ── Legacy ── */}
            <Route path="/profile"              element={<ProtectedPublic><Profile /></ProtectedPublic>} />
            <Route path="/checkout"             element={<ProtectedPublic><CheckoutPage /></ProtectedPublic>} />
            <Route path="/paiement/:id"         element={<ProtectedPublic><PaiementPage /></ProtectedPublic>} />
            {/* 🔒 RC-3 : page de confirmation PUBLIQUE. Les DONNÉES sont protégées au niveau API
                (getPaymentDetails/ticket exigent le jeton — invité en sessionStorage OU vrai compte).
                Ainsi l'invité voit son billet SANS être « connecté » (isAuthenticated=false → pas
                d'espace compte). Un vrai compte y accède comme avant via son propre jeton. */}
            <Route path="/paiement/success/:id" element={<PaiementSuccessPage />} />
            <Route path="/paiement/success"     element={<PaiementSuccessPage />} />
            <Route path="/paiement/en-attente"  element={<PaiementEnAttente />} />
            <Route path="/paiement/cancel"      element={<PaiementCancel />} />
            <Route path="/mes-reservations"     element={<ProtectedPublic><MyReservations /></ProtectedPublic>} />
            <Route path="/panier"               element={<ProtectedPublic><CartPage /></ProtectedPublic>} />

            <Route path="/unauthorized" element={<PageUnauthorized />} />
            <Route path="*"             element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </main>

      {!isProtectedRoute && <Footer />}

      {/* 📱 Navigation basse mobile (site public) — masquée en desktop via CSS. */}
      {!isProtectedRoute && <BottomNav />}

      {/* Bouton WhatsApp — public uniquement */}
      <a
        href="https://wa.me/2250720333997"
        target="_blank"
        rel="noopener noreferrer"
        title={t('whatsapp')}
        aria-label={t('whatsapp')}
        style={{
          position: 'fixed', bottom: '24px', right: '24px',
          width: '56px', height: '56px', borderRadius: '50%',
          background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(37,211,102,0.45)', zIndex: 9999,
          transition: 'transform 0.2s', textDecoration: 'none',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.12)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        <svg viewBox="0 0 32 32" width="32" height="32" fill="white" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M16 2C8.28 2 2 8.28 2 16c0 2.46.65 4.79 1.8 6.82L2 30l7.37-1.77A13.93 13.93 0 0 0 16 30c7.72 0 14-6.28 14-14S23.72 2 16 2zm0 25.5a11.45 11.45 0 0 1-5.85-1.6l-.42-.25-4.37 1.05 1.07-4.26-.28-.44A11.46 11.46 0 0 1 4.5 16C4.5 9.6 9.6 4.5 16 4.5S27.5 9.6 27.5 16 22.4 27.5 16 27.5zm6.3-8.55c-.34-.17-2.02-1-2.34-1.11-.32-.11-.55-.17-.78.17s-.9 1.11-1.1 1.34c-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.75-1.7-1.02-.9-1.7-2.02-1.9-2.36-.2-.34-.02-.52.15-.69.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6-.09-.17-.78-1.88-1.07-2.58-.28-.68-.57-.59-.78-.6h-.67c-.23 0-.6.09-.91.43-.32.34-1.2 1.18-1.2 2.87s1.23 3.33 1.4 3.56c.17.23 2.42 3.7 5.87 5.19.82.35 1.46.56 1.96.72.82.26 1.57.22 2.16.13.66-.1 2.02-.83 2.31-1.62.28-.8.28-1.48.2-1.62-.09-.14-.32-.23-.66-.4z"/>
        </svg>
      </a>
    </>
  );
};

// =============================================
// APP ROOT
// =============================================

function App() {
  const { t } = useTranslation('common');
  const [alert, setAlert] = useState({ show: false, type: "warning", message: "" });
  const [apiStatus, setApiStatus] = useState("checking");
  const [maintenance, setMaintenance] = useState({ active: false, message: '', until: null });
  // Compte les échecs consécutifs : la bannière n'apparaît qu'au 2e échec
  // pour absorber les démarrages à froid de Render free tier (30-60s)
  const failureCountRef = useRef(0);

  useEffect(() => {
    checkAPIStatus();
    const interval = setInterval(checkAPIStatus, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAPIStatus = async () => {
    // Onglet en arrière-plan → on ne sonde pas (comme les meilleurs sites : le travail de fond
    // se met en pause sur un onglet caché). La sonde reprendra au retour (visibilité/focus).
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    try {
      const result = await checkAPIHealth();

      try {
        const statusRes = await fetch(
          (process.env.REACT_APP_API_URL || 'https://api.gowele.com/api') + '/status'
        );
        const statusData = await statusRes.json();
        if (statusData.maintenance) {
          const msg = statusData.message || '';
          const until = statusData.until || null;   // ⏱️ échéance (ISO) → temps restant affiché
          // Guard : ne change l'état QUE s'il diffère → évite un re-render de toute l'app à
          // chaque poll (toutes les 2 min), qui pouvait cascader en remontage de la page.
          setMaintenance(prev => (prev.active && prev.message === msg && prev.until === until) ? prev : { active: true, message: msg, until });
          return;
        } else {
          setMaintenance(prev => (!prev.active && prev.message === '' && !prev.until) ? prev : { active: false, message: '', until: null });
        }
      } catch {
        // pas de maintenance si endpoint indisponible
      }

      if (result?.status === "online") {
        setApiStatus(prev => prev === "online" ? prev : "online");
        failureCountRef.current = 0;
        setAlert(prev => prev.show ? { ...prev, show: false } : prev);
      } else {
        setApiStatus("offline");
        failureCountRef.current += 1;
        if (failureCountRef.current >= 2) {
          setAlert({ show: true, type: "warning", message: t('alert.serverDown') });
        }
      }
    } catch {
      setApiStatus("error");
      failureCountRef.current += 1;
      if (failureCountRef.current >= 2) {
        setAlert({ show: true, type: "warning", message: t('alert.networkError') });
      }
    }
  };

  const handleAlertClose = () => setAlert(prev => ({ ...prev, show: false }));

  // Sélectionne le portail selon le domaine — déterminé une seule fois au montage
  const adminHost = isAdminHost();

  return (
    <HelmetProvider>
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <ToastProvider>
          <ConfirmProvider>
          <Router>
            <div className="App">
              {adminHost ? (
                <AdminPortal
                  alert={alert}
                  handleAlertClose={handleAlertClose}
                  maintenance={maintenance}
                />
              ) : (
                <PublicPortal
                  apiStatus={apiStatus}
                  alert={alert}
                  handleAlertClose={handleAlertClose}
                  maintenance={maintenance}
                />
              )}
            </div>
          <ScrollToTopButton />
          </Router>
          </ConfirmProvider>
          </ToastProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
